import { useEffect, useState, useRef, useCallback } from "react";
import {
  saveToState,
  loadFromState,
  resetState,
  getPaginatedData,
} from "../utils";
import { getNodeSetNodes, getNodeSets, getTopRelated } from "../api/quontxApi";
import AsyncSelect from "react-select/async";
import Select from "react-select";

const TopRelatedFilter = ({ propagnateData }) => {
  const propagnateFunctionRef = useRef(propagnateData);

  const [data, setData] = useState(null);
  const [hasNetworkError, setHasNetworkError] = useState(false);
  const [nodeOptions, setNodeOptions] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  const [activeNode, setActiveNode] = useState(null);
  const [activeNodeSet, setActiveNodeSet] = useState(null);
  const [activeSchema, setActiveSchema] = useState(null);

  const resetFilters = () => {
    resetState("topRelatedState", [
      {
        stateFunc: setActiveNode,
        nullVal: null,
      },
      {
        stateFunc: setActiveNodeSet,
        nullVal: null,
      },
      {
        stateFunc: setActiveSchema,
        nullVal: null,
      },
    ]);
    setHasNetworkError(false);
    setData(null);
  };

  useEffect(() => {
    loadFromState("topRelatedState", [
      {
        stateFunc: setActiveNode,
        storageObjectKey: "activeNode",
        defaultVal: null,
      },
      {
        stateFunc: setActiveNodeSet,
        storageObjectKey: "activeNodeSet",
        defaultVal: null,
      },
      {
        stateFunc: setActiveSchema,
        storageObjectKey: "activeSchema",
        defaultVal: null,
      },
    ]);

    const propagnateDataFunctionCopy = propagnateFunctionRef.current;
    return () => {
      propagnateDataFunctionCopy({});
    };
  }, []);

  const loadNodeOptions = useCallback(async () => {
    const nodes = await getPaginatedData(getNodeSetNodes, {
      itemsPerPage: 100,
      nodeSetId: activeNodeSet.uuid,
    });
    setNodeOptions(nodes.data);
  }, [activeNodeSet]);

  const loadNodeSetOptions = async (inputValue) => {
    if (!inputValue) return [];
    const nodeSets = await getNodeSets({
      itemsPerPage: 100,
      name: inputValue,
    });

    return nodeSets.data.data.map((node) => {
      return {
        label: node.description,
        value: node.uuid,
        uuid: node.uuid,
      };
    });
  };

  const loadTopRelated = useCallback(async () => {
    let topRelated;
    if (activeNode) {
      if (activeSchema.value === "ns2ns" || activeSchema.value === "ns2n")
        return;
      topRelated = await getTopRelated({
        k: 100,
        node: activeNode.value,
        schema: activeSchema.value,
      });
    } else if (activeNode === null && activeNodeSet) {
      if (activeSchema.value === "n2n") return;
      if (activeSchema.value === "ns2ns") {
        topRelated = await getTopRelated({
          k: 10,
          node: activeNodeSet.value,
          schema: activeSchema.value,
        });
      } else if (activeSchema.value === "ns2n") {
        topRelated = await getTopRelated({
          k: 100,
          node: activeNodeSet.value,
          schema: activeSchema.value,
        });
      }
    }
    setData(topRelated.data);
  }, [activeSchema, activeNode, activeNodeSet]);

  useEffect(() => {
    if (!activeNodeSet) return;
    (async () => {
      await loadNodeOptions();
    })();
  }, [activeNode, loadNodeOptions]);

  useEffect(() => {
    if (
      activeNode !== null ||
      activeNodeSet !== null ||
      activeSchema !== null
    ) {
      saveToState("topRelatedState", {
        activeNode: activeNode,
        activeNodeSet: activeNodeSet,
        activeSchema: activeSchema,
      });
    }
  }, [activeNode, activeNodeSet, activeSchema]);

  useEffect(() => {
    if (
      (activeNode === null && activeNodeSet === null) ||
      activeSchema === null
    )
      return;
    (async () => {
      setLoadingData(true);
      await loadTopRelated();
      setLoadingData(false);
    })();
  }, [activeNode, activeNodeSet, activeSchema]);

  useEffect(() => {
    if (activeNode) {
      setActiveSchema({
        label: "Node to Node",
        value: "n2n",
        isDisabled: !activeNode && activeNodeSet,
      });
    } else if (activeNode === null && activeNodeSet) {
      setActiveSchema({
        label: "Node Set to Node Set",
        value: "ns2ns",
        isDisabled: activeNode,
      });
    } else if (!activeNode && !activeNodeSet) {
      setActiveSchema(null);
    }
  }, [activeNode, activeNodeSet]);

  return (
    <div className="w-full flex flex-col items-center content-center">
      <div className="flex flex-col gap-4 items-center justify-center mb-8">
        <div className="flex flex-col gap-4 items-center justify-center mb-8 w-full">
          <div className="flex gap-4  justify-center mb-8">
            <div className="w-[500px] flex flex-col gap-2">
              <p>Node set</p>
              <AsyncSelect
                cacheOptions
                defaultOptions={[]}
                loadOptions={loadNodeSetOptions}
                onChange={(selected) => setActiveNodeSet(selected)}
                value={activeNodeSet}
                placeholder="Search node set..."
                isClearable
              />
            </div>
          </div>
          <div className="flex gap-4  justify-center mb-8">
            <div className="w-[500px] flex flex-col gap-2">
              <p>Node</p>
              <Select
                isSearchable={true}
                isDisabled={!activeNodeSet}
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    borderColor: state.isDisabled ? "red" : "grey",
                  }),
                }}
                value={activeNode}
                onChange={(selected) => setActiveNode(selected)}
                options={nodeOptions.map((node) => {
                  return { label: node.name, value: node.uuid };
                })}
                isClearable
              />
            </div>
          </div>
          <div className="flex gap-4  justify-center mb-8">
            <div className="w-[500px] flex flex-col gap-2">
              <p>Schema</p>
              <Select
                value={activeSchema}
                onChange={(selected) => setActiveSchema(selected)}
                options={[
                  {
                    label: "Node to Node",
                    value: "n2n",
                    isDisabled: !activeNode && activeNodeSet,
                  },
                  {
                    label: "Node Set to Node Set",
                    value: "ns2ns",
                    isDisabled: activeNode,
                  },
                  {
                    label: "Node Set to Node",
                    value: "ns2n",
                    isDisabled: activeNode && activeNodeSet,
                  },
                ]}
              />
            </div>
          </div>
          <div>
            <button
              onClick={() => resetFilters()}
              className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200"
            >
              Reset filters
            </button>
          </div>
        </div>
        {hasNetworkError && <p>Failed to load comparison data</p>}
        {loadingData && <p>Loading data...</p>}
        {!loadingData && data && (
          <div>
            <table className="min-w-full">
              <thead className="bg-qtx-dark">
                <tr>
                  <th></th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider font-bold">
                    Label
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider font-bold">
                    Node idx
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider font-bold">
                    Edge weight
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-qtx-gray">
                {data.flatMap((topRelated, idx) => (
                  <tr key={topRelated.label}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-white">
                      {idx + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-qtx-light-gray">
                      {topRelated.label}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-qtx-light-gray">
                      {topRelated.node_idx}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-qtx-light-gray">
                      {topRelated.edge_weight}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopRelatedFilter;
